/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product') || document.querySelector('.section-main-product') || document;
        this.mainProductForm = this.section.querySelector('.js-product') || document.querySelector('.js-product');
        this.buyButtons = this.mainProductForm?.querySelector('buy-buttons') || this.section.querySelector('buy-buttons');
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantContainer = this.querySelector('.sticky-atc__details__variant');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.mainProductForm?.querySelector('variant-picker'));

        // Scroll handler (throttled)
        this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);

        // Run once on load + after layout settles (page load fix)
        StickyAtc.handleScroll.call(this);
        requestAnimationFrame(() => StickyAtc.handleScroll.call(this));
        setTimeout(() => StickyAtc.handleScroll.call(this), 250);

        // Update on resize/orientation change (breakpoint flip, header height changes, etc.)
        this.onResize = throttle(() => StickyAtc.handleScroll.call(this), 100);
        window.addEventListener('resize', this.onResize);
        window.addEventListener('orientationchange', this.onResize);

        this.onVariantChange = this.onVariantChange.bind(this);
        this.updateImage = this.updateImage.bind(this);
        this.mainProductForm?.addEventListener('on:variant:change', this.onVariantChange);
        document.addEventListener('on:media-gallery:change', this.updateImage);

        this.updateImage();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('orientationchange', this.onResize);
        this.mainProductForm?.removeEventListener('on:variant:change', this.onVariantChange);
        document.removeEventListener('on:media-gallery:change', this.updateImage);
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        const variantId = this.buyButtons?.querySelector('input[name="id"]')?.value
          || this.mainProductForm?.form?.querySelector('[name="id"]')?.value;
        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            evt.preventDefault();
            theme.scrollToRevealElement(target);
          }
          return;
        }

        if (!this.mainProductForm?.form) return;

        // Validate main form
        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductForm.form.reportValidity()) {
          evt.preventDefault();
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductForm.form.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        // Copy data
        this.form.querySelectorAll('[data-copied]').forEach((el) => el.remove());
        const formData = new FormData(this.mainProductForm.form);
        for (const p of formData) {
          if (!this.form.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.form.append(input);
          }
        }
        super.handleSubmit(evt);
      }

      /**
       * Determine visibility after scroll.
       *
       * Mobile (<768px):
       * - Show sticky ATC when the real ATC/buy-buttons are NOT visible in the viewport.
       * - Hide sticky ATC when the real ATC/buy-buttons ARE visible.
       *
       * Desktop:
       * - Keeps original behavior (sticky hides while main buttons are "above" the sticky header threshold).
       */
      static handleScroll() {
        const isMobile = window.matchMedia('(max-width: 767px)').matches;

        const stickyHeader = document.querySelector('.pageheader--sticky');
        const headerHeight = stickyHeader
          ? document.querySelector('.section-header')?.offsetHeight || 0
          : 0;

        const buyButtonsRect = this.buyButtons?.getBoundingClientRect?.();
        if (!buyButtonsRect) return;

        // If the element isn't actually rendered (display:none, collapsed), treat as NOT visible
        const buyButtonsRendered = !!(this.buyButtons.offsetParent) && buyButtonsRect.height > 0;

        // Visible = intersects viewport (accounting for sticky header overlay at top)
        const viewportTop = headerHeight;
        const viewportBottom = window.innerHeight;

        const buyButtonsVisible =
          buyButtonsRendered &&
          buyButtonsRect.bottom > viewportTop &&
          buyButtonsRect.top < viewportBottom;

        if (isMobile) {
          // Hide sticky when real buttons are visible
          this.classList.toggle('sticky-atc--out', buyButtonsVisible);
        } else {
          // Original desktop logic
          this.classList.toggle('sticky-atc--out', buyButtonsRect.bottom > headerHeight);
        }

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Handle a change in variant on the page.
       * @param {Event} evt - Variant change event dispatched by variant-picker.
       */
      onVariantChange(evt) {
        const idInput = this.form?.querySelector('[name="id"]');
        const variant = evt.detail.variant;

        if (variant) {
          if (this.variantTitle) this.variantTitle.textContent = variant.title;
          if (this.variantContainer) this.variantContainer.hidden = false;
          if (idInput) idInput.value = variant.id;
        } else {
          if (this.variantContainer) this.variantContainer.hidden = true;
          if (this.variantTitle) this.variantTitle.textContent = '';
          if (idInput) idInput.value = '';
        }

        this.updateImage();
        this.updateAddToCartButton(variant);
        this.setErrorMsgState();

        // Variant changes can affect layout/visibility of main ATC
        StickyAtc.handleScroll.call(this);
      }

      /**
       * Updates the image.
       */
      updateImage() {
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Updates the "Add to Cart" button label and status.
       * @param {object} variant - Current variant object.
       */
      updateAddToCartButton(variant) {
        if (!this.submitBtn) return;

        const variantAvailable = variant && variant.available;
        const unavailableStr = variant
          ? theme.strings.noStock : this.submitBtn.dataset.unavailableText;

        this.submitBtn.textContent = variantAvailable
          ? this.submitBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}

(() => {
  const mobileMq = window.matchMedia('(max-width: 767px)');
  let frameRequested = false;

  const updateStickyAtcVisibility = () => {
    frameRequested = false;

    if (!mobileMq.matches) return;

    document.querySelectorAll('.sticky-atc').forEach((stickyAtc) => {
      const section = stickyAtc.closest('.section-main-product') || document;
      const buyButtons = section.querySelector('.buy-buttons-row');
      if (!buyButtons) return;

      const rect = buyButtons.getBoundingClientRect();
      const buyButtonsVisible = rect.bottom > 0 && rect.top < window.innerHeight;

      stickyAtc.classList.toggle('sticky-atc--out', buyButtonsVisible);
    });
  };

  const requestUpdate = () => {
    if (frameRequested) return;

    frameRequested = true;
    requestAnimationFrame(updateStickyAtcVisibility);
  };

  const initMobileStickyAtcFallback = () => {
    requestUpdate();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    window.addEventListener('orientationchange', requestUpdate);

    if (typeof mobileMq.addEventListener === 'function') {
      mobileMq.addEventListener('change', requestUpdate);
    } else if (typeof mobileMq.addListener === 'function') {
      mobileMq.addListener(requestUpdate);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobileStickyAtcFallback, { once: true });
  } else {
    initMobileStickyAtcFallback();
  }
})();
